import { useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useAuthUser } from "react-auth-kit";
import { useGetDevices } from "../../DeviceComponents";
import { usePutKitDevices } from "../useKitQueries";
import { isDeviceEligibleForKit } from "../kitDeviceUtils";
import { prettify, renderDeviceName } from "../../../utils/utils";
import { LoadingError, LoadingPaper } from "../../LoadingComponents";

function AddKitDevicesDialog({
  open,
  kitId,
  orgId,
  currentDeviceIds = [],
  onClose,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const authUser = useAuthUser();
  const isSuperAdmin = authUser()?.role === 1;
  const devicesQuery = useGetDevices("all");
  const putKitDevices = usePutKitDevices(kitId);

  const allDevices = devicesQuery.data?.devices ?? [];
  const eligibleDevices = allDevices.filter((d) => {
    if (d.kit_id != null && d.kit_id !== "") return false;
    if (d.patient_id != null && d.patient_id !== "") return false;
    if (!isSuperAdmin) return true;
    return isDeviceEligibleForKit(d, orgId);
  });

  const handleClose = () => {
    setSelectedIds([]);
    onClose();
  };

  const handleDeviceIdsChange = (event) => {
    const value = event.target.value;
    setSelectedIds(
      typeof value === "string" ? value.split(",").map(Number) : value
    );
  };

  const renderSelectedDevices = (selected) =>
    selected
      .map((id) => {
        const d = allDevices.find((el) => Number(el.id) === Number(id));
        return d ? prettify(d.name) : id;
      })
      .join(", ");

  const handleConfirm = () => {
    if (selectedIds.length === 0) return;
    const device_ids = [
      ...new Set([
        ...currentDeviceIds.map(Number),
        ...selectedIds.map(Number),
      ]),
    ];
    putKitDevices.mutate({ device_ids }, { onSuccess: handleClose });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Aggiungi strumenti al kit</DialogTitle>
      <DialogContent>
        {devicesQuery.isLoading ? (
          <LoadingPaper />
        ) : devicesQuery.isError ? (
          <LoadingError error={devicesQuery.error} />
        ) : eligibleDevices.length === 0 ? (
          <Typography sx={{ mt: 1 }}>
            Nessuno strumento disponibile (stessa organizzazione, senza kit e
            senza paziente).
          </Typography>
        ) : (
          <TextField
            select
            autoFocus
            fullWidth
            margin="dense"
            label="Strumenti"
            variant="standard"
            value={selectedIds}
            onChange={handleDeviceIdsChange}
            SelectProps={{
              multiple: true,
              renderValue: renderSelectedDevices,
            }}
            helperText="Gli strumenti selezionati si aggiungono a quelli già nel kit (invio elenco completo al server)"
          >
            {eligibleDevices.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                <Checkbox checked={selectedIds.includes(d.id)} />
                <ListItemText
                  primary={prettify(d.name)}
                  secondary={renderDeviceName(d.device_type)}
                />
              </MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          disabled={
            putKitDevices.isLoading ||
            selectedIds.length === 0 ||
            eligibleDevices.length === 0
          }
          onClick={handleConfirm}
        >
          Aggiungi
        </Button>
        <Button onClick={handleClose} color="error">
          Annulla
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddKitDevicesDialog;
